import React, { ChangeEvent, FC, ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import ReviewsIcon from '@mui/icons-material/Reviews';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  CellContext,
  ColumnHelper,
  defineColumns,
  TanstackTable,
  useModal,
  useToast,
} from '@thingslog/ui-components';
import { ScheduledEventSummary } from './ScheduledEventSummary';
import { ScheduledEventDto } from '@thingslog/repositories';
import { useViewport } from '../../../hooks';

interface RowAction {
  icon: ReactNode;
  label: string;
  onClick: () => void;
}

const RowActionsMenu: FC<{ actions: RowAction[] }> = ({ actions }: { actions: RowAction[] }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        size="small"
        onClick={(e: React.MouseEvent<HTMLButtonElement>): void => setAnchorEl(e.currentTarget)}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={(): void => setAnchorEl(null)}>
        {actions.map((action: RowAction, index: number) => (
          <MenuItem
            key={index}
            onClick={(): void => {
              action.onClick();
              setAnchorEl(null);
            }}
          >
            <ListItemIcon>{action.icon}</ListItemIcon>
            <ListItemText>{action.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const ScheduledTasks: FC<ScheduledTasksProps> = (props: ScheduledTasksProps) => {
  const { t } = useTranslation();

  const { modal, closeModal } = useModal();
  const { toast } = useToast();
  const isMobile = useViewport('sm');

  const handleViewScheduledEvent = (data: ScheduledEventDto): void => {
    modal({
      title: data.eventName,
      content: <ScheduledEventSummary data={data} />,
    });
  };

  const handleDeleteScheduledEvent = (id: number, isActive: boolean): void => {
    if (isActive) {
      toast({
        message: t('task_scheduler_remove_active'),
        type: 'error',
        duration: 3000,
      });
    } else {
      modal({
        title: t('task_scheduler_remove_confirmation'),
        content: (
          <div className="flex flex-col min-w-[320px]">
            <p>{t('task_scheduler_action_not_reversible')}</p>
            <div className="flex justify-end gap-0.5 items-center">
              <Button
                variant="contained"
                onClick={(): void => {
                  props.onDelete(id);
                  closeModal();
                }}
              >
                {t('task_scheduler_confirm')}
              </Button>
              <Button variant="outlined" onClick={closeModal}>
                {t('task_scheduler_cancel')}
              </Button>
            </div>
          </div>
        ),
      });
    }
  };

  const columns = useMemo(
    () =>
      defineColumns<ScheduledEventDto>((column: ColumnHelper<ScheduledEventDto>) => {
        const boldHeader = (label: string): ReactNode => (
          <Typography variant={isMobile ? 'body2' : 'body1'} className="font-semibold">
            {label}
          </Typography>
        );
        const bodyText = (value: ReactNode): ReactNode => (
          <Typography variant={isMobile ? 'body2' : 'body1'} className="text-gray-800">
            {value}
          </Typography>
        );

        return [
          column.accessor('eventName', {
            header: (): ReactNode => boldHeader(t('scheduled_tasks_list_event_name')),
            meta: {
              filterVariant: 'text',
              label: t('scheduled_tasks_list_event_name'),
              grow: true,
              minWidth: 180,
            },
            cell: (info: CellContext<ScheduledEventDto, string>): ReactNode =>
              bodyText(info.getValue()),
          }),
          column.accessor('startDate', {
            header: (): ReactNode => boldHeader(t('scheduled_tasks_list_start_date')),
            meta: {
              filterVariant: 'text',
              label: t('scheduled_tasks_list_start_date'),
              minWidth: 180,
            },
            cell: (info: CellContext<ScheduledEventDto, Date>): ReactNode =>
              bodyText(info.getValue() != null ? String(info.getValue()) : ''),
          }),
          column.accessor('endDate', {
            header: (): ReactNode => boldHeader(t('scheduled_tasks_list_end_date')),
            meta: {
              filterVariant: 'text',
              label: t('scheduled_tasks_list_end_date'),
              minWidth: 180,
            },
            cell: (info: CellContext<ScheduledEventDto, Date>): ReactNode =>
              bodyText(info.getValue() != null ? String(info.getValue()) : ''),
          }),
          column.accessor('executionTime', {
            header: (): ReactNode => boldHeader(t('scheduled_tasks_list_execution_time')),
            meta: {
              filterVariant: 'text',
              label: t('scheduled_tasks_list_execution_time'),
              minWidth: 150,
            },
            cell: (info: CellContext<ScheduledEventDto, string>): ReactNode =>
              bodyText(info.getValue()),
          }),
          column.accessor('lastExecutionDate', {
            header: (): ReactNode => boldHeader(t('scheduled_tasks_list_last_execution')),
            meta: {
              filterVariant: 'date',
              label: t('scheduled_tasks_list_last_execution'),
              minWidth: 220,
            },
            cell: (info: CellContext<ScheduledEventDto, string>): ReactNode => {
              const value = info.getValue();
              return bodyText(value ? format(new Date(value), 'HH:mm dd/MM/yyyy') : '');
            },
          }),
          column.accessor('active', {
            header: (): ReactNode => boldHeader(t('scheduled_tasks_list_is_active')),
            enableColumnFilter: false,
            enableSorting: false,
            meta: {
              label: t('scheduled_tasks_list_is_active'),
              align: 'center',
              headerAlign: 'center',
              minWidth: 100,
            },
            cell: (info: CellContext<ScheduledEventDto, boolean>): ReactNode => (
              <Switch
                checked={info.row.original.active}
                onChange={(event: ChangeEvent<HTMLInputElement>): void =>
                  props.onChangeStatus(info.row.original.id, event.target.checked)
                }
                size="small"
              />
            ),
          }),
          column.display({
            id: 'actions',
            header: '',
            meta: { align: 'right', minWidth: 56 },
            cell: (info: CellContext<ScheduledEventDto, unknown>): ReactNode => {
              const row = info.row.original;
              return (
                <RowActionsMenu
                  actions={[
                    {
                      icon: <HistoryIcon />,
                      label: t('scheduled_tasks_event_logs_btn_text'),
                      onClick: (): void => props.onViewLogs(row.id),
                    },
                    {
                      icon: <ReviewsIcon />,
                      label: t('scheduled_tasks_view_btn_text'),
                      onClick: (): void => handleViewScheduledEvent(row),
                    },
                    {
                      icon: <EditIcon />,
                      label: t('task_scheduler_edit'),
                      onClick: (): void => props.onEdit(row.id),
                    },
                    {
                      icon: <DeleteIcon />,
                      label: t('task_scheduler_remove'),
                      onClick: (): void => handleDeleteScheduledEvent(row.id, row.active),
                    },
                  ]}
                />
              );
            },
          }),
        ];
      }),
    [t, isMobile]
  );

  return (
    <TanstackTable<ScheduledEventDto>
      mode="client"
      className="w-full"
      data={props.rows}
      columns={columns}
      getRowId={(row: ScheduledEventDto): string => `${row.id}`}
      isLoading={props.isLoading}
      density="compact"
      classNames={{ container: 'max-h-[30rem]' }}
      pageSizeOptions={[10, 25, 50]}
      initialState={{
        sorting: [{ id: 'lastExecutionDate', desc: true }],
        pagination: { pageIndex: 0, pageSize: 10 },
      }}
      emptyMessage={t('scheduled_tasks_no_data', 'No scheduled tasks')}
    />
  );
};

interface ScheduledTasksProps {
  rows: ScheduledEventDto[];
  isLoading?: boolean;
  onChangeStatus: (id: number, active: boolean) => void;
  onDelete: (id: number) => void;
  onViewLogs: (id: number) => void;
  onEdit: (id: number) => void;
}

export default ScheduledTasks;
